import type { AdminJourneyEvent, JoinFailureReason, JourneyEventName } from "@ternary/contracts"

/**
 * A signup's stages in the order the pipeline reaches them. Step views and failed joins are
 * events, not stages; the dashboard's funnel (§3.5) also leaves out `discord_started`.
 */
export const JOURNEY_STEPS = [
  "landing",
  "form_start",
  "details_submitted",
  "email_verified",
  "discord_started",
  "discord_authorized",
  "joined",
] as const

type Description = { title: string; tone: "neutral" | "success" | "danger" }

const TITLES: Record<Exclude<JourneyEventName, "step_view">, Description> = {
  landing: { title: "Landed on the signup page", tone: "neutral" },
  form_start: { title: "Started the form", tone: "neutral" },
  details_submitted: { title: "Submitted details", tone: "neutral" },
  email_verified: { title: "Confirmed email", tone: "neutral" },
  discord_started: { title: "Went to Discord", tone: "neutral" },
  discord_authorized: { title: "Connected Discord", tone: "neutral" },
  joined: { title: "Joined the server", tone: "success" },
  join_failed: { title: "Join failed", tone: "danger" },
}

/** Where an event sits in the pipeline: a step view after the landing, a failed join at the join. */
function stage(event: JourneyEventName) {
  if (event === "step_view") return JOURNEY_STEPS.indexOf("landing") + 0.5
  if (event === "join_failed") return JOURNEY_STEPS.indexOf("joined")
  return JOURNEY_STEPS.indexOf(event)
}

/**
 * Oldest first. Events in the same moment read in pipeline order, then by id, so the Timeline
 * never depends on the order the API sent them in.
 */
export function compareJourneyEvents(a: AdminJourneyEvent, b: AdminJourneyEvent): number {
  return (
    Date.parse(a.at) - Date.parse(b.at) ||
    stage(a.event) - stage(b.event) ||
    (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
  )
}

export type FunnelRow = { step: string; label: string; count: number; dropOff: number | null }

/**
 * The dashboard's funnel (§3.5) in pipeline order, stages this build does not know last. Each
 * row's drop-off is the share of the row before that it lost, or null for the first row and after
 * a row of 0. The stages are not nested (ruling R62): landing counts touches, the others signups,
 * each in the range, so a stage can count more than the one before. Its drop-off is then 0.
 */
export function funnelRows(funnel: { step: string; count: number }[]): FunnelRow[] {
  const order = (step: string) => {
    const index = (JOURNEY_STEPS as readonly string[]).indexOf(step)
    return index === -1 ? JOURNEY_STEPS.length : index
  }
  const sorted = [...funnel].sort((a, b) => order(a.step) - order(b.step))
  return sorted.map(({ step, count }, index) => {
    const previous = sorted[index - 1]?.count
    return {
      count,
      dropOff: previous ? Math.max(0, 1 - count / previous) : null,
      label: Object.hasOwn(TITLES, step) ? TITLES[step as keyof typeof TITLES].title : step,
      step,
    }
  })
}

/** Why Discord refused a join, as the owner reads it on the Timeline (G4). */
const JOIN_FAILURE_TITLES: Record<JoinFailureReason, string> = {
  missing_permission: "Join failed: Ternary is missing a permission",
  role_hierarchy: "Join failed: Ternary can't give a role",
}

/** How a step view names its step: the signup's step ids, with Discord a proper noun. */
const STEP_LABELS: Record<string, string> = { details: "details", discord: "Discord", join: "join" }

/** What a journey event says on the customer's Timeline, and the colour of its dot. */
export function describeJourneyEvent(event: AdminJourneyEvent): Description {
  if (event.event === "join_failed" && event.reason) {
    return { title: JOIN_FAILURE_TITLES[event.reason], tone: "danger" }
  }
  if (event.event !== "step_view") return TITLES[event.event]
  return {
    title: event.step
      ? `Viewed the ${Object.hasOwn(STEP_LABELS, event.step) ? STEP_LABELS[event.step] : event.step} step`
      : "Viewed a step",
    tone: "neutral",
  }
}
